/* api/enums — bidirectional mapping between frontend display strings and
   backend SCREAMING_SNAKE_CASE enum values.

   Usage:
     import { LOAN_TYPE, mapBe, normPerson } from './enums';
     const display = LOAN_TYPE.toFe['PERSONAL_LOAN'];  // → 'Personal Loan'
     const beValue = LOAN_TYPE.toBe['Personal Loan'];  // → 'PERSONAL_LOAN'
*/

const bidi = (entries) => ({
  toFe: Object.fromEntries(entries),
  toBe: Object.fromEntries(entries.map(([be, fe]) => [fe, be])),
});

export const LOAN_TYPE = bidi([
  ['PERSONAL_LOAN',  'Personal Loan'],
  ['HOME_LOAN',      'Home Loan'],
  ['AUTO_LOAN',      'Auto Loan'],
  ['BUSINESS_LOAN',  'Business Loan'],
  ['EDUCATION_LOAN', 'Education Loan'],
  ['GOLD_LOAN',      'Gold Loan'],
]);

export const EMPLOYMENT_TYPE = bidi([
  ['SALARIED',       'Salaried'],
  ['SELF_EMPLOYED',  'Self Employed'],
  ['RETIRED',        'Retired'],
  ['BUSINESS_OWNER', 'Business Owner'],
]);

export const REPAYMENT_STATUS = bidi([
  ['ON_TIME',      'On Time'],
  ['OVERDUE',      'Overdue'],
  ['NPA',          'NPA'],
  ['WRITTEN_OFF',  'Written Off'],
]);

export const RISK_LEVEL = bidi([
  ['LOW',      'Low'],
  ['MEDIUM',   'Medium'],
  ['HIGH',     'High'],
  ['CRITICAL', 'Critical'],
]);

export const ORG_ROLE = bidi([
  ['ADMIN',  'Org Admin'],
  ['MEMBER', 'Member'],
]);

export const WS_ROLE = bidi([
  ['WORKSPACE_ADMIN', 'Workspace Admin'],
  ['WORKSPACE_USER',  'Workspace User'],
]);

export const PLATFORM_ROLE = bidi([
  ['SUPER_ADMIN', 'Super Admin'],
  ['USER',        'User'],
]);

/* effectiveRole from GET /session/context → frontend ROLES display string */
export const EFFECTIVE_ROLE = bidi([
  ['SUPER_ADMIN',      'Super Admin'],
  ['ORG_ADMIN',        'Organization Admin'],
  ['WORKSPACE_ADMIN',  'Workspace Admin'],
  ['WORKSPACE_USER',   'Workspace User'],
]);

export const PARTNER_TYPE = bidi([
  ['IMPLEMENTATION', 'Implementation'],
  ['RESELLER',       'Reseller'],
  ['TECHNOLOGY',     'Technology'],
  ['STRATEGIC',      'Strategic'],
]);

export const PARTNER_TIER = bidi([
  ['PLATINUM', 'Platinum'],
  ['GOLD',     'Gold'],
  ['SILVER',   'Silver'],
  ['BRONZE',   'Bronze'],
]);

export const LICENSE_TYPE = bidi([
  ['USER',  'User'],
  ['BOT',   'Bot'],
  ['AGENT', 'Agent'],
]);

export const TASK_STATUS = bidi([
  ['OPEN',        'open'],
  ['IN_PROGRESS', 'in_progress'],
  ['COMPLETED',   'done'],
  ['CANCELLED',   'cancelled'],
]);

export const TASK_PRIORITY = bidi([
  ['LOW',    'Low'],
  ['MEDIUM', 'Medium'],
  ['HIGH',   'High'],
  ['URGENT', 'Urgent'],
]);

export const ORG_STATUS = bidi([
  ['ACTIVE',    'active'],
  ['SUSPENDED', 'suspended'],
  ['TRIAL',     'trial'],
  ['PENDING',   'pending'],
]);

export const DOMAIN_STATUS = bidi([
  ['VERIFIED',   'VERIFIED'],
  ['PENDING',    'PENDING'],
  ['UNVERIFIED', 'UNVERIFIED'],
]);

export const COMPANY_LIFECYCLE = bidi([
  ['LEAD',       'Lead'],
  ['ACCOUNT',    'Account'],
  ['ENTERPRISE', 'Enterprise'],
]);

export const PERSON_STATUS = bidi([
  ['NEW',       'New'],
  ['ENGAGED',   'Engaged'],
  ['QUALIFIED', 'Qualified'],
  ['CUSTOMER',  'Customer'],
]);

/* Helper: map a backend enum value to frontend display (or return original) */
export const mapFe = (mapping, value) => (value != null ? (mapping.toFe[value] ?? value) : '');
export const mapBe = (mapping, value) => (value != null ? (mapping.toBe[value] ?? value) : undefined);

/* ──────────────────────────────────────────────────
   Normalizers: backend response → frontend record shape
   ────────────────────────────────────────────────── */

const AVATAR_TONES = ['#125AB0', '#0E8C5A', '#A8508A', '#C26A1F', '#6E3FBA', '#B5740E'];
const toneFor = (str) => AVATAR_TONES[(str || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];

export const normPerson = (p) => ({
  id:               p.id,
  salutation:       p.salutation       || null,
  name:             p.name,
  email:            p.email            || '',
  phone:            p.phone            || '',
  role:             p.role             || '',
  company:          p.company || p.companyName || '',
  companyId:        p.companyId        || null,
  score:            p.score            ?? 50,
  status:           mapFe(PERSON_STATUS, p.status) || 'New',
  owner:            p.owner            || '',
  city:             p.city             || '',
  linkedin:         p.linkedin         || '',
  tags:             p.tags             || [],
  tone:             p.avatarTone       || toneFor(p.name),
  lastActivity:     p.lastActivityAt   ? new Date(p.lastActivityAt).toLocaleDateString('en-IN') : '',
  createdAt:        p.createdAt        || null,
  updatedAt:        p.updatedAt        || null,
  creditScore:      p.creditScore      ?? null,
  loanType:         mapFe(LOAN_TYPE,         p.loanType),
  employmentType:   mapFe(EMPLOYMENT_TYPE,   p.employmentType),
  repaymentStatus:  mapFe(REPAYMENT_STATUS,  p.repaymentStatus),
  riskLevel:        mapFe(RISK_LEVEL,        p.riskLevel),
  income:           p.income           ?? null,
  sourceSystem:     p.sourceSystem     || null,
  externalId:       p.externalId       || null,
  notes:            '',
});

export const normCompany = (c) => ({
  id:               c.id,
  name:             c.name,
  domain:           c.domain           || '',
  industry:         c.industry         || '',
  employees:        c.employees        ?? null,
  revenue:          c.revenue          || '',
  location:         c.location         || '',
  lifecycle:        mapFe(COMPANY_LIFECYCLE, c.lifecycle) || c.lifecycle || 'Lead',
  tags:             c.tags             || [],
  score:            c.score            ?? 50,
  deals:            c.dealCount        ?? 0,
  owner:            c.owner            || '',
  parentCompanyId:  c.parentCompanyId  || null,
  lastActivity:     c.lastActivityAt   ? new Date(c.lastActivityAt).toLocaleDateString('en-IN') : '',
  createdAt:        c.createdAt        || null,
  updatedAt:        c.updatedAt        || null,
});

export const normDeal = (d) => ({
  id:          d.id,
  title:       d.title,
  value:       d.value        ?? 0,
  currency:    d.currency     || 'INR',
  stageId:     d.stageId,
  stageName:   d.stageName    || d.stage?.name  || '',
  companyId:   d.companyId    || null,
  company:     d.companyName  || d.company?.name || '',
  contactId:   d.contactId    || d.person?.id   || null,
  contact:     d.contactName  || d.person?.name  || '',
  ownerId:     d.ownerId      || null,
  owner:       d.ownerName    || d.owner?.name   || '',
  priority:    mapFe(TASK_PRIORITY, d.priority)  || 'Medium',
  probability: d.probability  ?? 0,
  closeDate:   String(d.expectedCloseDate || d.closeDate || '').split('T')[0],
  createdAt:   d.createdAt    || null,
  updatedAt:   d.updatedAt    || null,
  tags:        d.tags         || [],
  score:          d.score          ?? 0,
  lastActivity:   d.lastActivityAt ? new Date(d.lastActivityAt).toLocaleDateString('en-IN') : '',
  stageEnteredAt: d.stageEnteredAt || null,
  nextAction:     d.nextAction     || null,
});

export const normTask = (t) => ({
  id:          t.id,
  title:       t.title,
  done:        t.status === 'COMPLETED',
  status:      mapFe(TASK_STATUS, t.status),
  dueDate:     t.dueDate     ? String(t.dueDate).split('T')[0] : '',
  priority:    mapFe(TASK_PRIORITY, t.priority) || t.priority || 'Medium',
  assignee:    t.assignee?.name ?? t.assigneeName ?? '',
  assigneeId:  t.assigneeId  || null,
  personId:    t.personId    || null,
  companyId:   t.companyId   || null,
  dealId:      t.dealId      || null,
  notes:       t.description || '',
  archived:    !!t.archived,
  completedAt: t.completedAt || null,
  createdAt:   t.createdAt   || null,
  updatedAt:   t.updatedAt   || null,
});

export const normDocument = (d) => ({
  id:         d.id,
  name:       d.fileName    || d.name  || '',
  type:       d.mimeType    || d.type  || 'file',
  size:       d.fileSize    ? (d.fileSize > 1048576 ? (d.fileSize / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(d.fileSize / 1024)) + ' KB') : (d.size || 0),
  url:        d.url         || '',
  uploadedAt: d.createdAt   ? new Date(d.createdAt).toLocaleDateString('en-IN') : '',
  uploadedBy: d.uploadedBy  || d.uploaderName || '',
  personId:   d.personId    || null,
  companyId:  d.companyId   || null,
  dealId:     d.dealId      || null,
  taskId:     d.taskId      || null,
});

export const normMember = (m) => ({
  id:      m.id,
  org:     m.orgId,
  name:    m.name,
  email:   m.email,
  orgRole: mapFe(ORG_ROLE, m.orgRole),
  tone:    toneFor(m.name),
  status:  m.status || 'active',
  userId:  m.userId || null,
});

export const normWorkspace = (w) => ({
  id:      w.id,
  org:     w.orgId,
  name:    w.name,
  members: w.memberCount ?? 0,
  modules: w.modules     || [],
  status:  w.status      || 'active',
  created: w.createdAt   ? String(w.createdAt).split('T')[0] : '',
});

export const normOrg = (o) => ({
  id:        o.id,
  name:      o.name,
  industry:  o.industry  || '',
  country:   o.country   || '',
  timezone:  o.timezone  || '',
  owner:     o.ownerName || '',
  ownerEmail: o.ownerEmail || '',
  plan:      o.plan      || 'Starter',
  status:    mapFe(ORG_STATUS, o.status),
  users:     o.memberCount ?? 0,
  region:    o.region    || '',
  created:   o.createdAt ? String(o.createdAt).split('T')[0] : '',
  partnerId: o.partnerId || null,
});

export const normPartner = (p) => ({
  id:      p.id,
  name:    p.name,
  type:    mapFe(PARTNER_TYPE, p.type),
  tier:    mapFe(PARTNER_TIER, p.tier),
  contact: p.contactName  || '',
  email:   p.contactEmail || '',
  region:  p.region       || '',
  orgs:    p.orgCount     ?? 0,
  status:  p.status       || 'active',
  since:   p.createdAt    ? String(p.createdAt).split('T')[0] : '',
});

export const normLicense = (l) => ({
  id:       l.id,
  org:      l.orgId,
  type:     mapFe(LICENSE_TYPE, l.type),
  allotted: l.allotted,
  used:     l.used,
  renews:   l.renewsAt ? String(l.renewsAt).split('T')[0] : '',
});

export const normAuditEntry = (a) => ({
  id:     a.id,
  who:    a.actorName  || 'System',
  action: a.action,
  target: a.target     || '',
  cat:    a.cat,
  sev:    a.sev,
  time:   a.createdAt  ? new Date(a.createdAt).toLocaleDateString('en-IN') : '',
});
